import { useMemo } from 'react';

import { Box, Button, Skeleton, Stack, alpha } from '@mui/material';
import { Handshake, Languages, Phone } from 'lucide-react';

import { getPhoneLabel } from '@modules/_shared/components/PhoneInput';
import {
  EditableChip,
  chipStyle,
} from '@modules/_shared/components/visibility-drawer/edit-modals/EditableChip';
import { YearsInBusinessIcon } from '@modules/_shared/components/visibility-drawer/edit-modals/YearsInBusinessIcon';
import { useDictionaryLabels } from '@modules/_shared/hooks/useDictionaryLabels';
import { getAgentDictionaryType } from '@modules/_shared/utils/agentDictionaryFields';
import { formatYearsInBusiness } from '@modules/_shared/utils/formatting';
import { useLocale } from '@shared/hooks/locale';

type UserProfileBadgesProps = {
  hiresOnRealAdvisor?: number | null;
  activeSince?: number | null;
  languages?: string[] | null;
  phone?: string | null;
  onEditHiresOnRealAdvisor?: () => void;
  onEditYearsInBusiness?: () => void;
  onEditLanguages?: () => void;
  onEditPhone?: () => void;
};

export const AgentProfileBadges = ({
  hiresOnRealAdvisor,
  activeSince,
  languages,
  phone,
  onEditHiresOnRealAdvisor,
  onEditYearsInBusiness,
  onEditLanguages,
  onEditPhone,
}: UserProfileBadgesProps) => {
  const { t } = useLocale();
  const {
    labels: languageLabels,
    loading: languagesLoading,
    error: languagesError,
  } = useDictionaryLabels(languages, getAgentDictionaryType('languages'));

  const hiresLabel = `${hiresOnRealAdvisor ?? '-'} ${t(
    'Hires on RealAdvisor',
  )}`;

  const languageLabel = languagesError
    ? t('Failed to load data')
    : languageLabels.length > 0
    ? languageLabels.join(', ')
    : '-';

  const yearsInBusiness = useMemo(
    () => formatYearsInBusiness(activeSince, t),
    [activeSince, t],
  );

  return (
    <Stack direction="column" spacing={1.5} sx={{ mt: 0 }}>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1.5,
          ...chipStyle,
        }}
      >
        <EditableChip
          variant="outlined"
          sx={{
            backgroundColor: theme => alpha(theme.palette.primary.main, 0.1),
            border: 'none',
            color: 'primary.main',
            '&:hover': {
              backgroundColor: theme =>
                `${alpha(theme.palette.primary.main, 0.2)} !important`,
            },
            '& .MuiChip-deleteIcon': {
              color: 'primary.main',
              mr: 1,
            },
          }}
          icon={<Handshake size={14} />}
          label={hiresLabel}
          onEdit={onEditHiresOnRealAdvisor}
          color="primary"
        />

        <EditableChip
          icon={<YearsInBusinessIcon />}
          label={yearsInBusiness.label}
          onEdit={onEditYearsInBusiness}
        />

        {languagesLoading ? (
          <Skeleton variant="rounded" width={160} height={32} />
        ) : (
          <EditableChip
            sx={{
              ...(languagesError && {
                color: 'error.main',
                borderColor: 'error.main',
              }),
            }}
            icon={<Languages size={14} />}
            label={languageLabel}
            onEdit={onEditLanguages}
          />
        )}
      </Box>

      <Button
        variant="contained"
        color="primary"
        startIcon={<Phone />}
        onClick={onEditPhone}
        sx={{ alignSelf: 'flex-start' }}
      >
        {getPhoneLabel(phone, t('Add phone number'))}
      </Button>
    </Stack>
  );
};
